/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef, useState } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';

import IframeBlockerPage from 'bundles/embedded-experience/components/IframeBlockerPage';
import { createSsoRelayId, isSsoRelayMessage } from 'bundles/embedded-experience/lib/ssoPopupRelay';

import _t from 'i18n!nls/embedded-experience';

type Props = {
  thirdPartyOrganizationId: string;
  /** Path + query to land on after sign-in; passed through the SSO flow as `redirectTo`. */
  returnTo: string;
};

type Status = 'idle' | 'waiting' | 'closed' | 'blocked';

const POPUP_NAME = 'courseraSsoLogin';
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 650;
const POPUP_CLOSED_POLL_MS = 500;

// Centered over the browser window, like Google and Apple sign-in popups.
const getPopupFeatures = () => {
  const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2);
  return `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-300);
    min-height: 100vh;
    padding: var(--cds-spacing-400);
    text-align: center;
    background-color: var(--cds-color-neutral-background-primary);
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    max-width: 449px;
    color: var(--cds-color-neutral-primary);
  `,
};

/**
 * Sign-in for a framed C4B launch. IDP login pages refuse to render in a frame, so the
 * learner signs in through a popup; the popup relays the single-use SSO token back here and this
 * frame redeems it on the SSO return page, which mints a partitioned session for the iframe.
 */
const EmbeddedSsoPopupLogin = ({ thirdPartyOrganizationId, returnTo }: Props) => {
  const [status, setStatus] = useState<Status>('idle');
  const popupRef = useRef<Window | null>(null);
  const relayIdRef = useRef('');

  useEffect(() => {
    if (status !== 'waiting') {
      return undefined;
    }
    let relayed = false;
    const handleMessage = (event: MessageEvent) => {
      if (!isSsoRelayMessage(event, relayIdRef.current, popupRef.current)) {
        return;
      }
      relayed = true;
      const { method, token } = event.data;
      // Carry the launch `source` so edge still lets the LMS frame this page after the
      // CSOD_TRANSIT_CONTEXT cookie has expired (e.g. the learner idled on this screen).
      const source = new URLSearchParams(window.location.search).get('source');
      const query = source ? `?source=${encodeURIComponent(source)}` : '';
      window.location.assign(`/sso/return/${encodeURIComponent(method)}/${encodeURIComponent(token)}${query}`);
    };
    const pollId = window.setInterval(() => {
      if (!relayed && popupRef.current?.closed) {
        setStatus('closed');
      }
    }, POPUP_CLOSED_POLL_MS);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(pollId);
    };
  }, [status]);

  const openPopup = () => {
    const relayId = createSsoRelayId();
    const params = new URLSearchParams({
      id: thirdPartyOrganizationId,
      redirectTo: encodeURIComponent(returnTo),
      ssoRelayId: relayId,
    });
    // Must run synchronously in the click handler, or the browser blocks the popup.
    const popup = window.open(`/sso/redirect?${params.toString()}`, POPUP_NAME, getPopupFeatures());
    if (!popup) {
      setStatus('blocked');
      return;
    }
    relayIdRef.current = relayId;
    popupRef.current = popup;
    setStatus('waiting');
  };

  if (status === 'blocked') {
    return (
      <div css={styles.container}>
        <IframeBlockerPage variant="ssoFallback" continueHref={window.location.href} />
      </div>
    );
  }

  return (
    <div css={styles.container} data-testid="EmbeddedSsoPopupLogin">
      <div css={styles.textBlock}>
        <Typography2 component="h1" variant="titleMedium">
          {_t('Sign in to continue')}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" aria-live="polite">
          {status === 'closed'
            ? _t('The sign-in window closed before you finished. Try again to continue.')
            : _t('Sign in with your organization account. The sign-in page opens in a new window.')}
        </Typography2>
      </div>
      <Button variant="primary" size="medium" onClick={openPopup}>
        {status === 'idle' && _t('Sign in')}
        {status === 'waiting' && _t('Open sign-in window again')}
        {status === 'closed' && _t('Try again')}
      </Button>
      <div role="status">{status === 'waiting' && _t('Finish signing in in the new window.')}</div>
    </div>
  );
};

export default EmbeddedSsoPopupLogin;
